import { User } from './auth'

export type SubscriptionProductPrice = {
	id: string
	createdAt: string
	updatedAt: string
	name: string
	currency: string
	amount: number
	trialAmount: number
	trialDays: number
	stripeId: string
	paypalId: string
	paypalSubscriptionPriceId: string
	productId: string
	duration: number
	durationUnits: string
	stripePublicKey: string
	stripeAccountCountry: string
}

export type CreateSubscriptionRequestParams = {
	priceId: string
	subscriptionId?: string
	paymentMethodId?: string
	confirmRemovalPendingPaypalSubscription?: boolean
}

export type CreatePayPalSubscriptionResponse = {
	status: string
	approveLink: string
}

export type CreateStripeSubscriptionResponse = {
	status: string
	clientSecret: string
	user: User
}

export enum StripeSubscriptionResponseStatusEnum {
	ACTIVE = 'active',
	PAST_DUE = 'past_due',
	UNPAID = 'unpaid',
	CANCELED = 'canceled',
	INCOMPLETE = 'incomplete',
	INCOMPLETE_EXPIRED = 'incomplete_expired',
	TRIALING = 'trialing',
}

export enum DURATION_CONTEXT {
	DAYS = 'days',
	WEEKS = 'weeks',
	MONTHS = 'months',
	YEARS = 'years',
}

export type SubscriptionProductVariables = {
	currency: string
	currencySymbol: string
	titlePrice: string
	titlePriceFormatted: string
	trialPrice: number
	trialPriceFormatted: string
	trialDuration: number
	trialContext: `${DURATION_CONTEXT}`
	regularPrice: number
	regularPriceFormatted: string
	regularDuration: number
	regularContext: `${DURATION_CONTEXT}`
	compareAtPrice: number
	compareAtPriceFormatted: string
	discountPercentage: number
}

export type CurrentSubscription = Subscription | null

export type SubscriptionProduct = {
	id: string
	name: string
	createdAt: string
	updatedAt: string
	description: string
	priceTitle: string
	sku: string
	isFavourite: boolean
	price: SubscriptionProductPrice
	priceNumber: number
	priceDecimal: number
	label: string
	productVariables: SubscriptionProductVariables
	recurrentName: string
	applePayDisabled?: boolean
	googlePayDisabled?: boolean
}

export type Currency = {
	code: string
	symbol: string
	isDefault: boolean
}

export type CurrencyState = {
	currency: Currency
	loaded: boolean
	currencyList: Currency[]
}

export type Subscription = {
	id: string
	createdAt: string
	updatedAt: string
	provider: string
	providerSubscriptionId: string
	status: SubscriptionStatusEnum
	onTrial?: boolean
	expiresAt: string
	priceId: string
	userId: string
	product: SubscriptionProduct
}

export enum SubscriptionStatusEnum {
	ACTIVE = 'active',
	EXPIRED = 'expired',
	CANCELLED = 'cancelled',
	INCOMPLETE = 'incomplete',
	INITIAL = 'initial',
}

export enum UserStatusEnum {
	FREEMIUM_NEW = 'freemium_new',
	FREEMIUM_OLD = 'freemium_old',
	NEVER_PAID = 'never_paid',
	PREMIUM = 'premium',
	PREMIUM_GIFTED = 'premium_gifted',
	PREMIUM_CANCELLING = 'premium_cancelling',
	PREMIUM_CANCELLED = 'premium_cancelled',
	PREMIUM_UNPAID = 'premium_unpaid',
	PREMIUM_IN_DEBT = 'premium_in_debt',
	DEACTIVATED = 'deactivated',
}

export enum UserMarketingFlowStatusEnum {
	LOGGEDIN = 'loggedin', // User was displayed the download popup
	LOGGEDIN_PLANS = 'loggedin_plans', // User visited the plans page
	DOWNLOADED = 'downloaded',
	SENT_SECOND_EMAIL = 'sent_second_email', // Scan & Track or Don't forget to download
	SENT_PRINT_EMAIL = 'sent_print_email',
	FINISHED = 'finished',
}

export enum FlowTypeEnum {
	SOME_DAYS_FREE = 'some-days-free',
	UNLOCK = 'unlock',
}

export const PaidUserStatuses = [
	UserStatusEnum.PREMIUM,
	UserStatusEnum.PREMIUM_GIFTED,
	UserStatusEnum.PREMIUM_UNPAID,
]

export enum PaymentFailCause {
	BACKEND = 'backend',
	THIRD_PARTY = '3rd-party',
}

export type PaymentDetailsWallet = 'apple_pay' | 'google_pay'

export type PaymentDetails = {
	type: string
	last4: string
	provider: string
	expirationDate: string
	wallet?: PaymentDetailsWallet
}

export type CardMaskType = 'bullet' | 'star'
